/**
 * O que faz: Renderiza artigo público por slug com layout editorial, compartilhamento, índice lateral, FAQ, fontes e CTA.
 * Onde usa: Rota pública `/blog/:slug`.
 * Conexões: Usa React Router params, useBlogPost, ReactMarkdown, LeadCaptureForm e schema BlogPosting/FAQ.
 * Tabelas: blog_posts (SELECT público filtrado por slug e status approved via useBlogPost).
 * Data: 12/05/2026
 */

import { isValidElement, useState, type ReactNode } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  ArrowRight,
  Bookmark,
  CheckCircle2,
  ChevronDown,
  Clock3,
  Copy,
  Lightbulb,
  Sparkles,
} from "lucide-react";
import PublicContentLayout from "@/components/blog/PublicContentLayout";
import { LeadCaptureForm } from "@/components/blog/LeadCaptureForm";
import { StructuredData, buildBlogPostingSchema, buildFaqSchema } from "@/components/seo/StructuredData";
import { mapBlogPostToPublicContentItem } from "@/content/blogPostMapper";
import { useBlogPost } from "@/hooks/useBlogPosts";
import NotFound from "./NotFound";

const SITE_URL = "https://crm.agenciagens.com.br";

type ArticleHeading = {
  id: string;
  title: string;
};

const WhatsAppBrandIcon = () => (
  <svg className="public-share-brand-icon" viewBox="0 0 32 32" aria-hidden="true" focusable="false">
    <path
      fill="currentColor"
      d="M16.04 3.2c-7.08 0-12.84 5.7-12.84 12.72 0 2.24.6 4.44 1.74 6.36L3.08 29l6.9-1.8a12.94 12.94 0 0 0 6.06 1.52c7.08 0 12.84-5.7 12.84-12.72S23.12 3.2 16.04 3.2Zm0 23.36c-1.9 0-3.76-.5-5.38-1.45l-.39-.23-4.1 1.07 1.1-3.98-.26-.41a10.5 10.5 0 0 1-1.62-5.64c0-5.82 4.78-10.56 10.65-10.56 5.88 0 10.66 4.74 10.66 10.56 0 5.82-4.78 10.64-10.66 10.64Zm5.84-7.9c-.32-.16-1.9-.93-2.2-1.04-.3-.1-.52-.16-.74.16-.22.32-.85 1.04-1.04 1.26-.19.22-.38.24-.7.08-.32-.16-1.36-.5-2.6-1.6-.96-.85-1.6-1.9-1.8-2.22-.18-.32-.02-.5.14-.66.15-.15.32-.38.48-.57.16-.19.22-.32.32-.53.1-.22.05-.4-.03-.56-.08-.16-.74-1.78-1.01-2.44-.27-.64-.54-.55-.74-.56h-.63c-.22 0-.56.08-.86.4-.3.32-1.12 1.1-1.12 2.66 0 1.57 1.15 3.08 1.31 3.3.16.21 2.26 3.43 5.47 4.8.76.33 1.36.53 1.83.68.77.24 1.47.2 2.02.12.62-.09 1.9-.78 2.17-1.53.27-.75.27-1.4.19-1.53-.08-.13-.3-.21-.62-.37Z"
    />
  </svg>
);

const LinkedInBrandIcon = () => (
  <svg className="public-share-brand-icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      fill="currentColor"
      d="M20.45 20.45h-3.56v-5.57c0-1.33-.02-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.95v5.66H9.34V9h3.41v1.56h.05c.48-.9 1.64-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28ZM5.32 7.43a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12Zm1.78 13.02H3.54V9H7.1v11.45ZM22.22 0H1.77C.79 0 0 .77 0 1.72v20.56C0 23.23.79 24 1.77 24h20.45c.98 0 1.78-.77 1.78-1.72V1.72C24 .77 23.2 0 22.22 0Z"
    />
  </svg>
);

const getTextFromChildren = (children: ReactNode): string => {
  if (typeof children === "string" || typeof children === "number") return String(children);
  if (Array.isArray(children)) return children.map(getTextFromChildren).join("");
  if (isValidElement<{ children?: ReactNode }>(children)) return getTextFromChildren(children.props.children);
  return "";
};

const normalizeHeadingId = (value: string) =>
  value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");

const estimateReadingTime = (body: string) => {
  // Motivo: tempo de leitura ajuda o usuário a decidir se continua no artigo antes de rolar a página.
  const wordCount = body.trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(wordCount / 180));
};

const normalizeMarkdownForArticle = (body: string) =>
  // Motivo: posts importados do Notion usam "---" como divisor visual; em Markdown isso pode transformar o parágrafo anterior em H2 setext.
  body.replace(/^\s*---\s*$/gm, "***");

const extractHeadings = (body: string): ArticleHeading[] =>
  body
    .split(/\r?\n/)
    .filter((line) => line.startsWith("## ") && !line.startsWith("### "))
    .map((line) => line.replace(/^##\s+/, "").trim())
    .filter(Boolean)
    .map((title) => ({ id: normalizeHeadingId(title), title }));

const markdownComponents: Components = {
  h2: ({ children }) => {
    const title = getTextFromChildren(children);
    return (
      <h2 id={normalizeHeadingId(title)}>
        <span>{children}</span>
      </h2>
    );
  },
  blockquote: ({ children }) => (
    <aside className="public-article-callout">
      <Lightbulb size={26} />
      <div>{children}</div>
    </aside>
  ),
  p: ({ children }) => {
    const text = getTextFromChildren(children);
    const isOperationalCard = ["O que é:", "Como calcular:", "Qual o número bom?"].some((marker) => text.includes(marker));

    return <p className={isOperationalCard ? "public-article-insight-card" : undefined}>{children}</p>;
  },
  ul: ({ children, className }) => (
    <ul className={["public-article-list", className].filter(Boolean).join(" ")}>{children}</ul>
  ),
  li: ({ children, className }) => (
    <li className={["public-article-list-item", className].filter(Boolean).join(" ")}>{children}</li>
  ),
  hr: () => <div className="public-article-divider" aria-hidden="true" />,
};

const BlogPostPage = () => {
  const { slug = "" } = useParams();
  const { data: post, isError, isLoading } = useBlogPost(slug);
  const [copied, setCopied] = useState(false);
  const [saved, setSaved] = useState(false);

  if (isLoading) {
    return (
      <PublicContentLayout title="Carregando artigo" description="Carregando conteúdo do blog.">
        <section className="public-section">
          <p className="public-card-meta">Carregando artigo...</p>
        </section>
      </PublicContentLayout>
    );
  }

  // Retorna 404 amigável quando o slug não existe ou ainda não foi aprovado.
  if (isError || !post) return <NotFound />;

  const item = mapBlogPostToPublicContentItem(post);
  // Monta a URL canonica pela rota real do artigo para impedir compartilhamento sem o prefixo /blog.
  const canonical = `${SITE_URL}/blog/${post.slug}`;
  const articleBody = normalizeMarkdownForArticle(item.body);
  const readingTime = estimateReadingTime(item.body);
  const headings = extractHeadings(articleBody);

  const shareLinks = {
    whatsapp: `https://wa.me/?text=${encodeURIComponent(`${item.title} - ${canonical}`)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(canonical)}`,
  };

  const copyCanonical = async () => {
    // Motivo: copiar link reduz atrito de compartilhamento quando o usuário prefere enviar manualmente.
    await navigator.clipboard.writeText(canonical);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  return (
    <PublicContentLayout title={item.title} description={item.description} showHero={false}>
      <Helmet>
        <title>{item.title} | Blog Gens CRM</title>
        <meta name="description" content={item.description} />
        <link rel="canonical" href={canonical} />
      </Helmet>
      <StructuredData data={buildBlogPostingSchema(item)} />
      <StructuredData data={buildFaqSchema(item)} />

      <div className="public-blog-shell">
        <nav className="public-breadcrumb" aria-label="Breadcrumb">
          <Link to="/blog">Blog</Link>
          <ChevronDown size={13} />
          <Link to="/blog">{item.category}</Link>
          <ChevronDown size={13} />
          <span>{item.title}</span>
        </nav>

        <div className="public-blog-layout">
          <main className="public-blog-main">
            <section className="public-post-hero">
              <div className="public-post-hero-copy">
                <div className="public-post-meta-row">
                  <span className="public-category-chip">{item.category}</span>
                  <span>Atualizado em {item.updatedAt}</span>
                  <span>
                    <Clock3 size={15} />
                    {readingTime} min de leitura
                  </span>
                </div>
                <h1>{item.title}</h1>
                <p>{item.description}</p>
                <div className="public-share-row" aria-label="Compartilhar artigo">
                  <span>Compartilhar</span>
                  <a
                    href={shareLinks.whatsapp}
                    target="_blank"
                    rel="noreferrer"
                    className="public-share-brand public-share-whatsapp"
                    aria-label="Compartilhar no WhatsApp"
                    title="WhatsApp"
                  >
                    <WhatsAppBrandIcon />
                  </a>
                  <a
                    href={shareLinks.linkedin}
                    target="_blank"
                    rel="noreferrer"
                    className="public-share-brand public-share-linkedin"
                    aria-label="Compartilhar no LinkedIn"
                    title="LinkedIn"
                  >
                    <LinkedInBrandIcon />
                  </a>
                  <button type="button" onClick={copyCanonical} aria-label="Copiar link" title="Copiar link">
                    <Copy size={16} />
                  </button>
                  <button
                    type="button"
                    className="public-save-button"
                    onClick={() => setSaved((current) => !current)}
                    aria-pressed={saved}
                  >
                    <Bookmark size={16} />
                    {saved ? "Salvo" : "Salvar artigo"}
                  </button>
                  {copied ? <small>Link copiado</small> : null}
                </div>
              </div>
            </section>

            <article className="public-article public-article-premium">
              <ReactMarkdown components={markdownComponents} remarkPlugins={[remarkGfm]}>
                {articleBody}
              </ReactMarkdown>
            </article>

            <section id="perguntas-frequentes" className="public-section public-faq-section">
              <h2>Perguntas frequentes</h2>
              <div className="public-faq-accordion">
                {item.faqs.map((faq) => (
                  <details key={faq.question} className="public-faq-item">
                    <summary>
                      {faq.question}
                      <ChevronDown size={17} />
                    </summary>
                    <p>{faq.answer}</p>
                  </details>
                ))}
              </div>
            </section>

            <section className="public-final-cta">
              <div className="public-final-cta-icon">
                <Sparkles size={34} />
              </div>
              <div className="public-final-cta-copy">
                <h2>Menos perdas, mais controle e uma clínica que cresce.</h2>
                <p>Conheça o Gens CRM e organize leads, retornos, agenda e relatórios em uma rotina mais simples.</p>
              </div>
              <div className="public-final-cta-actions">
                <a href="#blog-lead-form" className="public-final-primary">
                  Começar grátis
                  <ArrowRight size={16} />
                </a>
                <a href="#blog-lead-form" className="public-final-secondary">
                  Agendar demonstração
                </a>
                <small>Pagamento seguro • Planos mensais • Cancelamento fácil</small>
              </div>
            </section>

            <section className="public-section public-sources-section">
              <h2>Fontes</h2>
              <ul className="public-list">
                {item.sources.map((source) => (
                  <li key={source.url}>
                    <a href={source.url} target="_blank" rel="noreferrer">
                      {source.label}
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          </main>

          <aside className="public-blog-sidebar">
            <section className="public-sidebar-card public-toc-card public-hero-toc-card">
              <h2>Neste artigo</h2>
              <ol>
                {headings.map((heading, index) => (
                  <li key={heading.id} className={index === 0 ? "is-active" : ""}>
                    <a href={`#${heading.id}`}>{heading.title}</a>
                  </li>
                ))}
                <li>
                  <a href="#perguntas-frequentes">Perguntas frequentes</a>
                </li>
              </ol>
            </section>

            <section className="public-sidebar-card public-sidebar-cta">
              <h2>Veja como o Gens CRM ajuda sua clínica</h2>
              <p>Preencha seus dados para abrir uma conversa contextual pelo WhatsApp.</p>
              <ul>
                <li>
                  <CheckCircle2 size={15} />
                  Lembretes e follow-up
                </li>
                <li>
                  <CheckCircle2 size={15} />
                  Funil comercial para WhatsApp
                </li>
                <li>
                  <CheckCircle2 size={15} />
                  Relatórios de leads e agenda
                </li>
              </ul>
              <LeadCaptureForm
                id="blog-lead-form"
                variant="compact"
                submitLabel="Quero ver na prática"
                ctaContext={item.ctaContext}
                sourceUrl={canonical}
              />
            </section>

            <section className="public-sidebar-card public-results-card">
              <h2>Resultados que importam</h2>
              <div>
                <strong>60%</strong>
                <span>Redução média de perdas</span>
              </div>
              <div>
                <strong>+25%</strong>
                <span>Aumento de receita</span>
              </div>
              <div>
                <strong>+20h</strong>
                <span>Economia operacional</span>
              </div>
              <p>*Resultados observados em rotinas comerciais acompanhadas.</p>
            </section>
          </aside>
        </div>
      </div>
    </PublicContentLayout>
  );
};

export default BlogPostPage;
